// src/pages/Contact.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  ArrowLeft, Phone, Mail, MapPin, Send, MessageSquare,
  User, AlertCircle
} from 'lucide-react';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import SuccessModal from '../components/SuccessModal';

const Contact = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [pageText, setPageText] = useState({
    contactTitle: 'Get In Touch',
    contactSubtitle: 'Koi sawal, suggestion ya feedback? Humein message bhejein — hum jald jawab denge.',
    contactFormTitle: 'Send Us a Message',
    contactFormSubtitle: 'Hum 24 ghante mein jawab dete hain.',
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig(data);
          setPageText({
            contactTitle: data.contactTitle || 'Get In Touch',
            contactSubtitle: data.contactSubtitle || 'Koi sawal, suggestion ya feedback? Humein message bhejein — hum jald jawab denge.',
            contactFormTitle: data.contactFormTitle || 'Send Us a Message',
            contactFormSubtitle: data.contactFormSubtitle || 'Hum 24 ghante mein jawab dete hain.',
          });
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.name || !formData.email || !formData.message) {
      setMessage({ type: 'error', text: 'Name, Email aur Message zaroori hai.' });
      return;
    }

    setSending(true);
    try {
      await addDoc(collection(db, 'contactMessages'), {
        ...formData,
        read: false,
        createdAt: new Date(),
      });
      setShowSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      setMessage({ type: 'error', text: 'Message send nahi ho saka. Try again.' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  const phone = config?.phone || '+92 300 1234567';
  const email = config?.email || 'hello@organicland.com';
  const address = config?.address || 'Lahore, Pakistan';
  const whatsappNumber = phone.replace(/[^0-9]/g, '');

  return (
    <div className="min-h-screen anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-sm btn-2d shadow-sm"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      {/* HERO */}
      <section className="py-12 md:py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 mb-5 anim-fadeInUp">
            <MessageSquare size={16} style={{ color: 'var(--color-link)' }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-link)' }}>
              Contact Us
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight anim-fadeInUp delay-100"
            style={{ color: 'var(--color-primary-text)' }}>
            {pageText.contactTitle}
          </h1>

          <p className="text-base md:text-lg max-w-2xl mx-auto anim-fadeInUp delay-200"
            style={{ color: 'var(--color-secondary-text)' }}>
            {pageText.contactSubtitle}
          </p>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT SIDE - Contact Info */}
          <div className="lg:col-span-1 space-y-4">

            {/* Phone */}
            <a href={`tel:${phone}`}
              className="block rounded-2xl p-5 card-lift anim-fadeInLeft"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
                  <Phone size={22} style={{ color: 'var(--color-link)' }} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase mb-1"
                    style={{ color: 'var(--color-secondary-text)' }}>Call Us</p>
                  <p className="font-bold text-sm md:text-base"
                    style={{ color: 'var(--color-primary-text)' }}>{phone}</p>
                </div>
              </div>
            </a>

            {/* Email */}
            <a href={`mailto:${email}`}
              className="block rounded-2xl p-5 card-lift anim-fadeInLeft"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, animationDelay: '100ms' }}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-blue-50">
                  <Mail size={22} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase mb-1"
                    style={{ color: 'var(--color-secondary-text)' }}>Email Us</p>
                  <p className="font-bold text-sm md:text-base break-all"
                    style={{ color: 'var(--color-primary-text)' }}>{email}</p>
                </div>
              </div>
            </a>

            {/* Address */}
            <div className="rounded-2xl p-5 card-lift anim-fadeInLeft"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, animationDelay: '200ms' }}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-yellow-50">
                  <MapPin size={22} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase mb-1"
                    style={{ color: 'var(--color-secondary-text)' }}>Visit Us</p>
                  <p className="font-bold text-sm md:text-base"
                    style={{ color: 'var(--color-primary-text)' }}>{address}</p>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer"
              className="block rounded-2xl p-5 card-lift anim-fadeInLeft"
              style={{ backgroundColor: '#25D366', color: '#fff', animationDelay: '300ms' }}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <FaWhatsapp size={22} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase mb-1 opacity-90">WhatsApp</p>
                  <p className="font-bold text-sm md:text-base">Chat Now</p>
                </div>
              </div>
            </a>

            {/* Social Links */}
            <div className="rounded-2xl p-5 anim-fadeInLeft"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, animationDelay: '400ms' }}>
              <p className="text-xs font-semibold uppercase mb-3"
                style={{ color: 'var(--color-secondary-text)' }}>Follow Us</p>
              <div className="flex gap-3 flex-wrap">
                {config?.footerFacebook && (
                  <a href={config.footerFacebook} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition btn-2d bg-blue-50">
                    <FaFacebook size={18} className="text-blue-700" />
                  </a>
                )}
                {config?.footerInstagram && (
                  <a href={config.footerInstagram} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition btn-2d bg-pink-50">
                    <FaInstagram size={18} className="text-pink-600" />
                  </a>
                )}
                {config?.footerTwitter && (
                  <a href={config.footerTwitter} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition btn-2d bg-blue-50">
                    <FaTwitter size={18} className="text-blue-500" />
                  </a>
                )}
                {config?.footerYoutube && (
                  <a href={config.footerYoutube} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition btn-2d bg-red-50">
                    <FaYoutube size={18} className="text-red-600" />
                  </a>
                )}
                {!config?.footerFacebook && !config?.footerInstagram && !config?.footerTwitter && !config?.footerYoutube && (
                  <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>
                    Social links admin panel se add karein.
                  </p>
                )}
              </div>
            </div>

          </div>

          {/* RIGHT SIDE - Contact Form */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl p-6 md:p-8 anim-fadeInRight"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
                {pageText.contactFormTitle}
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--color-secondary-text)' }}>
                {pageText.contactFormSubtitle}
              </p>

              {message.text && (
                <div className={`text-sm p-3 rounded-lg mb-4 flex items-center gap-2 ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  <AlertCircle size={14} /> {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-primary-text)' }}>Your Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-3.5 text-gray-400" size={18} />
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
                        placeholder="Ahmed Khan" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-primary-text)' }}>Your Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3.5 text-gray-400" size={18} />
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
                        placeholder="ahmed@example.com" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-primary-text)' }}>Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3.5 text-gray-400" size={18} />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
                        placeholder="+92 300 1234567" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-primary-text)' }}>Subject</label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3.5 text-gray-400" size={18} />
                      <input type="text" name="subject" value={formData.subject} onChange={handleChange}
                        className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
                        placeholder="Kis baare mein hai?" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2"
                    style={{ color: 'var(--color-primary-text)' }}>Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={5}
                    className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none resize-none"
                    style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
                    placeholder="Apna message likhein..." />
                </div>

                <button type="submit" disabled={sending}
                  className={`w-full rounded-full py-4 font-bold flex items-center justify-center gap-2 btn-2d shadow-xl ripple-btn ${
                    sending ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
                  {sending ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                        style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} /> Send Message
                    </>
                  )}
                </button>

              </form>
            </div>
          </div>

        </div>
      </section>

      <div className="h-20 md:h-0"></div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Message Sent!"
        message="Shukriya! Hum 24 ghante mein aap se contact karenge."
        buttonText="OK"
      />
    </div>
  );
};

export default Contact;